import { useEffect, useState } from 'react';

import { formatDate } from 'date-fns/format';
import { CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';

import { Button } from '@/components/TailwindComponents/base/Button/Button';
import { Calendar } from '@/components/TailwindComponents/base/Calendar/Calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/TailwindComponents/base/Popover/Popover';
import {
  ScrollArea,
  ScrollBar,
} from '@/components/TailwindComponents/base/ScrollArea/ScrollArea';
import { cn } from '@/components/TailwindComponents/lib/utils';
import { DEFAULT_INPUT_DATE_TIME_FORMAT } from '@/components/TailwindComponents/utils/defaultInputDateFormats';

import { MonthYearPicker } from '../MonthYearPicker/MonthYearPicker';

interface DateTimePickerProps {
  date?: Date | undefined;
  onSelect?: (date: Date | undefined) => void;
  format?: string;
  width?: 'auto' | 'full' | 'fit' | 'sm' | 'md' | 'lg' | 'xl' | string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean | ((date: Date) => boolean);
  placeholder?: string;
  // MonthYearPicker specific props
  showMonthYearPicker?: boolean;
  monthYearPickerProps?: {
    showClearBtn?: boolean;
  };
}

export function DateTimePicker({
  date,
  onSelect,
  format = DEFAULT_INPUT_DATE_TIME_FORMAT,
  width = 'full',
  minDate,
  maxDate,
  disabled,
  placeholder = 'MM/DD/YYYY HH:mm',
  showMonthYearPicker = false,
  monthYearPickerProps = {},
}: DateTimePickerProps) {
  const [dateState, setDateState] = useState<Date | undefined>(undefined);
  const [isOpen, setIsOpen] = useState(false);
  const [displayMonth, setDisplayMonth] = useState<Date>(date || new Date());

  useEffect(() => {
    if (
      (date && (!dateState || date.getTime() !== dateState.getTime())) ||
      (!date && dateState)
    ) {
      setDateState(date);
    }
  }, [date]);

  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Width configuration
  const getWidthClass = () => {
    if (
      typeof width === 'string' &&
      !['auto', 'full', 'fit', 'sm', 'md', 'lg', 'xl'].includes(width)
    ) {
      return width; // Custom width
    }

    const widthMap = {
      auto: 'w-auto',
      full: 'w-full',
      fit: 'w-fit',
      sm: 'w-32',
      md: 'w-48',
      lg: 'w-64',
      xl: 'w-80',
    };

    return widthMap[width as keyof typeof widthMap] || 'w-auto';
  };

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      setDateState(selectedDate);
      onSelect?.(selectedDate);
    }
  };

  const handleTimeChange = (type: 'hour' | 'minute', value: string) => {
    if (dateState) {
      const newDate = new Date(dateState);
      if (type === 'hour') {
        newDate.setHours(parseInt(value));
      } else if (type === 'minute') {
        newDate.setMinutes(parseInt(value));
      }
      setDateState(newDate);
      onSelect?.(newDate);

      if (type === 'minute') {
        setIsOpen(false); // Close popover when minute is selected
      }
    }
  };

  const handleMonthYearChange = (value: string | Date | undefined) => {
    if (value) {
      let newDate: Date;
      if (typeof value === 'string') {
        // Parse MM/yyyy string format
        const [month, year] = value.split('/');
        newDate = new Date(parseInt(year), parseInt(month) - 1, 1);
      } else {
        newDate = value;
      }
      setDisplayMonth(newDate);
    }
  };

  const CustomCaption = ({
    displayMonth: captionDisplayMonth,
  }: {
    displayMonth: Date;
  }) => {
    if (showMonthYearPicker) {
      return (
        <div className="flex justify-center pt-1 relative items-center">
          {/* Previous Month Button */}
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute left-1"
            onClick={() => {
              const prevMonth = new Date(captionDisplayMonth);
              prevMonth.setMonth(prevMonth.getMonth() - 1);
              setDisplayMonth(prevMonth);
            }}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          {/* MonthYearPicker */}
          <MonthYearPicker
            value={captionDisplayMonth}
            onSelect={handleMonthYearChange}
            className="h-8 text-sm font-medium justify-center border-0 shadow-none p-0 hover:bg-transparent"
            {...monthYearPickerProps}
          />

          {/* Next Month Button */}
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute right-1"
            onClick={() => {
              const nextMonth = new Date(captionDisplayMonth);
              nextMonth.setMonth(nextMonth.getMonth() + 1);
              setDisplayMonth(nextMonth);
            }}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      );
    }
    return null;
  };

  const isDisabled = typeof disabled === 'boolean' ? disabled : false;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'justify-start text-left font-normal',
            width === 'full' ? 'w-full' : 'min-w-[200px] max-w-[300px]',
            width !== 'full' && getWidthClass(),
            !dateState && 'text-muted-foreground',
          )}
          disabled={isDisabled}
        >
          {dateState ? (
            formatDate(dateState, format)
          ) : (
            <span>{placeholder}</span>
          )}
          <CalendarIcon className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <div className="sm:flex">
          <Calendar
            mode="single"
            selected={dateState}
            onSelect={handleDateSelect}
            initialFocus
            fromDate={minDate}
            toDate={maxDate}
            month={displayMonth}
            onMonthChange={setDisplayMonth}
            disabled={typeof disabled === 'function' ? disabled : undefined}
            classNames={{
              day_selected:
                'bg-[#EE3E5C] hover:bg-[#EE3E5C] text-white hover:text-white focus:bg-[#EE3E5C]',
            }}
            components={{
              Caption: showMonthYearPicker
                ? ({ displayMonth: captionDisplayMonth }) => (
                    <CustomCaption displayMonth={captionDisplayMonth} />
                  )
                : undefined,
            }}
          />
          <div className="flex flex-col sm:flex-row sm:h-[300px] divide-y sm:divide-y-0 sm:divide-x">
            <ScrollArea className="w-72 sm:w-auto">
              <div className="flex sm:flex-col p-2">
                {hours.reverse().map((hour) => (
                  <Button
                    key={hour}
                    size="icon"
                    variant={
                      dateState && dateState.getHours() === hour
                        ? 'default'
                        : 'ghost'
                    }
                    className="sm:w-full shrink-0 aspect-square"
                    onClick={() => handleTimeChange('hour', hour.toString())}
                  >
                    {hour}
                  </Button>
                ))}
              </div>
              <ScrollBar orientation="horizontal" className="sm:hidden" />
            </ScrollArea>
            <ScrollArea className="w-64 sm:w-auto">
              <div className="flex sm:flex-col p-2">
                {Array.from({ length: 60 }, (_, i) => i).map((minute) => (
                  <Button
                    key={minute}
                    size="icon"
                    variant={
                      dateState && dateState.getMinutes() === minute
                        ? 'default'
                        : 'ghost'
                    }
                    className="sm:w-full shrink-0 aspect-square"
                    onClick={() =>
                      handleTimeChange('minute', minute.toString())
                    }
                  >
                    {minute.toString().padStart(2, '0')}
                  </Button>
                ))}
              </div>
              <ScrollBar orientation="horizontal" className="sm:hidden" />
            </ScrollArea>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
